import { differenceInCalendarDays, format, formatDistanceToNow } from 'date-fns'
import {
  PartsData,
  PartsTableEntries,
  PartsTableEntry,
  UnitAssetModelTable,
  UnitInspections,
} from 'features/partsDatabase/types/partsTableTypes'
// import { CustomField } from 'features/assetDatabase/types/customField'
import { ForecastDarkTheme } from 'styles/themes'
import { parseDate } from 'utils/parsingHelpers'

export const getCriticalityTagColor = (criticality: string) => {
  criticality = criticality?.toLowerCase()
  let color
  switch (criticality) {
    case 'low':
      color = ForecastDarkTheme.greenGlassBackground
      break
    case 'medium':
      color = ForecastDarkTheme.blueGlassBackground
      break
    case 'high':
      color = ForecastDarkTheme.redGlassBackground
      break
  }
  return color ?? ForecastDarkTheme.glassBackground
}

export const getAssetModels = (assetData: PartsTableEntries[] | undefined) => {
  return assetData
    ?.filter((asset) => asset.entry.type === 'asset-model')
    .map((unit) => {
      return {
        assetType: unit.entry.data.type,
        assetModelUnitTag: unit.entry.tags.unit,
        assetModelData: unit.entry.data,
        assetModelUpdatedAt: unit.entry.updated_at,
      }
    })
}

export const assetInspections = (
  assetData: PartsTableEntries[] | undefined
) => {
  return assetData
    ?.filter((asset) => asset.entry.type === 'inspection')
    .map((inspection) => {
      return {
        inspectionSlug: inspection.entry.id,
        inspectionDate: inspection.entry.data.date,
        inspectionUnitTag: inspection.entry.tags.unit,
        updatedAt: inspection.entry.updated_at,
      }
    })
}

// Finds the latest asset model for a given unit that isn't after today
const findCurrentUnitAssetModel = (
  unitAssetModels: UnitAssetModelTable[] | undefined,
  unitId: string
) => {
  return unitAssetModels
    ?.filter((unit) => unit.assetModelUnitTag === unitId)
    .sort((a, b) => {
      const dateA = parseDate(a.assetModelData.effective_date)
      const dateB = parseDate(b.assetModelData.effective_date)
      if (!dateA || !dateB) {
        return 0
      }
      return dateB.getTime() - dateA.getTime()
    })
    .find((unit) => {
      const effectiveDate = parseDate(unit.assetModelData.effective_date)
      if (!effectiveDate) {
        return false
      }
      return differenceInCalendarDays(effectiveDate, new Date()) <= 0
    })
}

// Finds the latest inspection for a given unit asset model (on or after effective date, but on or before today)
const findLatestInspection = (
  currentUnitAssetModel: UnitAssetModelTable | undefined,
  unitInspections: UnitInspections[] | undefined
): UnitInspections | undefined => {
  const effectiveDate = parseDate(
    currentUnitAssetModel?.assetModelData.effective_date
  )

  if (!effectiveDate || !unitInspections || unitInspections.length === 0) {
    return undefined
  }

  const unitTag = currentUnitAssetModel?.assetModelUnitTag

  const relevantInspections = unitInspections
    .filter((inspection) => {
      if (inspection.inspectionUnitTag !== unitTag) {
        return false
      }
      const inspectionDate = parseDate(inspection.inspectionDate)
      return (
        inspectionDate &&
        differenceInCalendarDays(inspectionDate, effectiveDate) >= 0 &&
        differenceInCalendarDays(inspectionDate, new Date()) <= 0
      )
    })
    .sort((a, b) => {
      const inspectionDateA = parseDate(a.inspectionDate)
      const inspectionDateB = parseDate(b.inspectionDate)

      // Handle potentially invalid dates during sort comparison
      if (!inspectionDateA && !inspectionDateB) return 0
      if (!inspectionDateA) return 1
      if (!inspectionDateB) return -1

      // Sort descending (latest first)
      return inspectionDateB.getTime() - inspectionDateA.getTime()
    })

  return relevantInspections.length > 0 ? relevantInspections[0] : undefined
}

export const convertDateToString = (
  dateInput: Date | undefined
): string | undefined => {
  if (dateInput !== undefined) {
    return format(dateInput, 'EEE MMM dd yyyy')
  } else {
    return undefined
  }
}

export const convertDateToDistanceToNow = (
  dateInput: Date | undefined
): string | undefined => {
  if (dateInput !== undefined) {
    const now = new Date()
    const oneDayInMs = 24 * 60 * 60 * 1000

    // Ensure dateInput is a Date object
    const dateObj = dateInput instanceof Date ? dateInput : parseDate(dateInput)
    if (!dateObj) {
      return undefined
    }

    // Check if the date is less than 1 day ago
    if (now.getTime() - dateObj.getTime() < oneDayInMs) {
      return formatDistanceToNow(dateObj, { addSuffix: true })
    } else {
      return convertDateToString(dateObj)
    }
  } else {
    return undefined
  }
}
const convertBlankToUndefined = (
  stringInput: string | undefined
): string | undefined => {
  if (stringInput?.trim() === '') {
    return undefined
  } else return stringInput?.trim()
}
export const createPartsTableData = (
  unitAssets: PartsTableEntry[],
  unitAssetModels: UnitAssetModelTable[],
  unitComponents: string[],
  unitInspections: UnitInspections[]
  // cardProps: AssetCardProps[]
) => {
  const result: PartsData[] = []
  unitAssets.forEach((unitAsset) => {
    const currentAssetModel = findCurrentUnitAssetModel(
      unitAssetModels,
      unitAsset.id
    )
    const latestInspection = findLatestInspection(
      currentAssetModel,
      unitInspections
    )

    const firstComponent = Object.values(
      currentAssetModel?.assetModelData.components ?? {}
    )[0]

    result.push({
      name: unitAsset.data.name,
      id: unitAsset.id,
      assetModelType: currentAssetModel?.assetType,
      lastInspectionDate: parseDate(latestInspection?.inspectionDate),
      lastInspectionSlug: latestInspection?.inspectionSlug,
      lastUpdated: parseDate(currentAssetModel?.assetModelUpdatedAt),
      componentType: firstComponent?.type,
      componentName: firstComponent?.name,
      stage: firstComponent?.stage,
      status: currentAssetModel?.assetModelData.status,
      effectiveDate: parseDate(
        currentAssetModel?.assetModelData.effective_date
      ),
      erpId: convertBlankToUndefined(unitAsset.data.erp_id),
      unitPrimaryImage: unitAsset.data.primary_image,
    })
  })
  result.sort((a, b) => {
    return a.name.localeCompare(b.name)
  })
  return result
}
